<template>
  <div>
    <input
      ref="imageInputRef"
      type="file"
      style="display:none"
      accept="image/*"
      @change="uploadFile"
    >
    <BaseOverlay :show="fetchingDetails">
      <b-card v-if="details && !fetchingDetails">
        <TransitionBusinessModal
          v-if="details"
          :id="`business-transition-modal-${details.name.en}`"
          :entity="details"
          :action="transitionBusinessData.action"
          entity-type="Business"
          @transition-applied="fetch"
        />
        <b-row>
          <b-col
            md="1"
            class="mb-1"
          >
            <div class="business-image__wrapper">
              <b-button
                class="business-image__wrapper-button"
                variant="flat-primary"
                size="sm"
                :disabled="uploadingFile"
                @click="browseFiles"
              >
                <b-spinner
                  v-if="uploadingFile"
                  small
                />
                <feather-icon
                  v-else
                  icon="UploadCloudIcon"
                />
              </b-button>
              <b-avatar
                size="64"
                :src="details.imageUrl"
              />
            </div>
          </b-col>
          <b-col md="2">
            <b-form-group label="Name En">
              <h5>{{ details.name.en }}</h5>
            </b-form-group>
          </b-col>
          <b-col md="2">
            <b-form-group label="Name Ar">
              <h5>{{ details.name.ar }}</h5>
            </b-form-group>
          </b-col>
          <b-col md="3">
            <b-form-group label="Email">
              <h5>{{ details.email }}</h5>
            </b-form-group>
          </b-col>
          <b-col md="2">
            <b-form-group label="Phone No.">
              <h5>{{ details.phoneNumber }}</h5>
            </b-form-group>
          </b-col>
          <b-col
            md="2"
          >
            <b-badge
              :variant="stateColor(details.state)"
              style="width:100%;padding:6px"
            >
              {{ snakeCaseToCamelCase(details.state) }}
            </b-badge>
            <div class="d-flex justify-content-around align-items-center">
              <b-button
                v-if="details.state == 'IN_REVIEW'"
                size="sm"
                class="btn-icon rounded-circle"
                variant="flat-success"
                @click="transitionBusinessHandler('APPROVE')"
              >

                <feather-icon
                  size="20"
                  icon="CheckIcon"
                />
              </b-button>
              <b-button
                v-if="details.state == 'IN_REVIEW'"
                size="sm"
                class="btn-icon rounded-circle"
                variant="flat-danger"
                @click="transitionBusinessHandler('REJECT')"
              >
                <feather-icon
                  size="20"
                  icon="XIcon"
                />

              </b-button>
              <b-button
                class="btn-icon rounded-circle"
                variant="flat-primary"
                @click="openUpdateBusinessForm = true"
              >
                <feather-icon
                  size="20"
                  icon="EditIcon"
                />
              </b-button>
            </div>
          </b-col>
          <template v-if="businessType == 'ride-hailing'">
            <b-col
              md="3"
              class="d-flex align-items-center"
            >
              <b-form-group
                label="Promo Prefix"
                class="mr-1"
              >
                <b-form-input
                  v-model="promoPrefix"
                  placeholder="Enter promo prefix"
                />
              </b-form-group>
              <b-button
                class="mt-1"
                variant="primary"
                :disabled="creatingBusinessPromo || !promoPrefix"
                @click="createBusinessPromoHandler"
              >
                <b-spinner
                  v-if="creatingBusinessPromo"
                  small
                />
                <span v-else>{{ hasPromoPrefix ? 'Edit' : 'Save' }}</span>
              </b-button>
            </b-col>
            <b-col
              md="9"
              class="d-flex justify-content-end align-items-center"
            >
              <b-button
                variant="danger"
                :to="`/businesses/versions/prepare-pricing?businessId=${businessId}`"
              >
                <feather-icon
                  icon="DollarSignIcon"
                  class="mr-1"
                />
                <span>Custom Pricing</span>
              </b-button>
            </b-col>
          </template>
        </b-row>
        <b-row v-if="openUpdateBusinessForm">
          <b-col md="12">
            <update-business-form
              :business-details="details"
              @updated="fetch"
            />
          </b-col>
        </b-row>
      </b-card>
    </BaseOverlay>
  </div>
</template>

<script>
import {
  BCard,
  BAvatar,
  BRow,
  BCol,
  BFormGroup,
  BSpinner,
  BButton,
  BFormInput,
  BBadge,
} from 'bootstrap-vue'
import { ref } from '@vue/composition-api'
import axios from 'axios'
import BaseOverlay from '@/UI/BaseOverlay.vue'
import snakeCaseToCamelCase from '@/utils/snake-case-to-camcel-caase'
import stateColor from '../hooks/state-coloring'
import formatDate from '@/utils/date-format'
import UpdateBusinessForm from './UpdateBusinessForm.vue'
import { errorNotification, successNotification } from '@/utils/toast-notifications'
import TransitionBusinessModal from './TransitionBusinessModal.vue'

export default {
  components: {
    TransitionBusinessModal,
    BCard,
    BaseOverlay,
    BAvatar,
    BFormInput,
    UpdateBusinessForm,
    BButton,
    BSpinner,
    BRow,
    BCol,
    BFormGroup,
    BBadge,
  },
  props: {
    businessType: {
      type: String,
      validator: val => ['restaurant', 'ride-hailing'].includes(val),
      default: 'restaurant',
    },
  },
  setup(_, ctx) {
    const businessId = ctx.root.$route.params.id

    const promoPrefix = ref('')
    const openUpdateBusinessForm = ref(false)
    const fetchingDetails = ref(false)
    const details = ref(undefined)
    const hasPromoPrefix = ref(false)
    const fetch = async () => {
      fetchingDetails.value = true
      const res = await ctx.root.$store.dispatch(
        'business/getBusinessDetails',
        {
          businessId,
        },
      )
      const { data } = res.data.data
      details.value = data
      hasPromoPrefix.value = !!data?.promoPrefix
      promoPrefix.value = data?.promoPrefix
      fetchingDetails.value = false
    }
    const imageInputRef = ref(null)
    const browseFiles = () => {
      imageInputRef.value.click()
    }
    const uploadingFile = ref(false)
    const uploadFile = async ev => {
      const file = ev.target.files[0]
      try {
        uploadingFile.value = true
        if (file) {
          const result = await ctx.root.$store.dispatch('business/getImagePresignedUrl', businessId)
          const { url, key } = result.data.data
          await axios.put(url, file)
          await ctx.root.$store.dispatch('business/updateBusiness', {
            businessId,
            data: {
              imageKey: key,
            },
          })
          successNotification('Business Image changed successfully')
          fetch()
        }
      } catch (error) {
        errorNotification('An error occured, try again')
      } finally {
        uploadingFile.value = false
        // eslint-disable-next-line no-param-reassign
        ev.target.value = ''
      }
    }

    const transitionBusinessData = ref({
      action: 'APPROVE', // APPROVE || REJECT
    })

    const transitionBusinessHandler = action => {
      transitionBusinessData.value.action = action
      ctx.root.$bvModal.show(`business-transition-modal-${details.value.name.en}`)
    }

    const creatingBusinessPromo = ref(false)
    const createBusinessPromoHandler = async () => {
      try {
        creatingBusinessPromo.value = true
        await ctx.root.$store.dispatch('business/createBusinessPrefix', {
          businessId,
          promoPrefix: promoPrefix.value,
        })
        successNotification('Promo prefix changed')
        hasPromoPrefix.value = true
      } finally {
        creatingBusinessPromo.value = false
      }
    }

    fetch()
    return {
      createBusinessPromoHandler,
      details,
      browseFiles,
      promoPrefix,
      imageInputRef,
      fetch,
      fetchingDetails,
      creatingBusinessPromo,
      hasPromoPrefix,
      transitionBusinessHandler,
      transitionBusinessData,
      uploadFile,
      snakeCaseToCamelCase,
      formatDate,
      stateColor,
      uploadingFile,
      businessId,
      openUpdateBusinessForm,
    }
  },
}
</script>

<style scoped>
.business-image__wrapper {
  position: relative;
}
.business-image__wrapper-button {
  position: absolute;
  top: -10px;
  left: -10px;
  z-index: 100;
  padding: 4px;
}
</style>
